import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ActivitiesApi } from "Data/ActivitiesApi";
import Button from "Components/UI/Button";
import { Spin } from "antd";

interface IViewpointTabProps {
  topicId: string;
  viewpointId: string;
  snapshotUrl?: string | null;
  loadingExternalLink: boolean;
  onViewInViewer?: () => void;
}

/**
 * ViewpointTab is responsible for loading and displaying the snapshot thumbnail
 * associated with an activity viewpoint. The component keeps the logic isolated,
 * heavily commented, and easy to maintain.
 */
const ViewpointTab: FC<IViewpointTabProps> = ({
  topicId,
  viewpointId,
  snapshotUrl: snapshotUrlProp,
  loadingExternalLink,
  onViewInViewer,
}) => {
  const { t } = useTranslation('activities');
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(snapshotUrlProp ?? null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // If the API already provided a direct snapshot URL we can skip any extra request.
    if (snapshotUrlProp) {
      setSnapshotUrl(snapshotUrlProp);
      setLoadingSnapshot(false);
      setErrorMessage(null);
      return;
    }

    // Bail out quickly when we do not have a valid combination of ids.
    if (!topicId || !viewpointId) {
      setSnapshotUrl(null);
      setErrorMessage(t('viewpoint.noViewpoint'));
      setLoadingSnapshot(false);
      return;
    }

    let canceled = false;
    setSnapshotUrl(null);
    setLoadingSnapshot(true);
    setErrorMessage(null);

    ActivitiesApi.getTopicViewpoint(topicId, viewpointId)
      .then((response) => {
        if (canceled) {
          return;
        }

        const base64 = response.data?.data?.InitialViewpoint?.SnapshotBase64;
        if (base64) {
          // Ensure the string is a valid data URL even when the backend returns raw base64.
          setSnapshotUrl(base64.startsWith("data:") ? base64 : `data:image/png;base64,${base64}`);
          setErrorMessage(null);
        } else {
          setSnapshotUrl(null);
          setErrorMessage(t('viewpoint.noSnapshot'));
        }
      })
      .catch(() => {
        if (canceled) {
          return;
        }
        setSnapshotUrl(null);
        setErrorMessage(t('viewpoint.loadError'));
      })
      .finally(() => {
        if (!canceled) {
          setLoadingSnapshot(false);
        }
      });

    // Prevent state updates when the component unmounts before the request finishes.
    return () => {
      canceled = true;
    };
  }, [topicId, viewpointId, snapshotUrlProp]);

  const showEmptyState = useMemo(() => {
    return !loadingSnapshot && (!snapshotUrl || !!errorMessage);
  }, [loadingSnapshot, snapshotUrl, errorMessage]);

  return (
    <Container>
      <SnapshotCard>
        {loadingSnapshot ? (
          <LoadingState>
            <Spin size="large" />
            <LoadingText>{t('viewpoint.loadingPreview')}</LoadingText>
          </LoadingState>
        ) : showEmptyState ? (
          <EmptyState>
            <EmptyTitle>{t('viewpoint.previewUnavailable')}</EmptyTitle>
            <EmptyDescription>
              {errorMessage ?? t('viewpoint.noPreview')}
            </EmptyDescription>
          </EmptyState>
        ) : (
          <ImageWrapper>
            <SnapshotImage src={snapshotUrl ?? ""} alt={t('attachmentsView.viewpointAlt')} />
          </ImageWrapper>
        )}
      </SnapshotCard>

      <HelperText>
        {t('viewpoint.helper')}
      </HelperText>

      <Button
        type="primary"
        height={40}
        disabled={loadingExternalLink || !topicId || !viewpointId}
        onClick={onViewInViewer}
      >
        {t('viewpoint.openViewer')}
      </Button>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  align-items: center;
`;

const SnapshotCard = styled.div`
  width: 100%;
  max-width: 560px;
  min-height: 280px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(148, 163, 184, 0.15);
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border-radius: 8px;
  overflow: hidden;
`;

const SnapshotImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 6px;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const LoadingText = styled.span`
  font-size: 14px;
  color: #4b5563;
`;

const EmptyState = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const EmptyDescription = styled.span`
  font-size: 13px;
  color: #6b7280;
`;

const HelperText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #4b5563;
  text-align: center;
`;

export default ViewpointTab;

